import { CSSProperties, MouseEventHandler, ReactNode } from "react";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "danger"
  | "transparent";
export type ButtonSize = "xsmall" | "small" | "medium" | "large";

export interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  style?: CSSProperties;
  type?: "button" | "submit" | "reset" | undefined;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  loading?: boolean;
  disabled?: boolean;
  active?: boolean;
  title?: string;
  children?: ReactNode;
  padding?: string;
  nowrap?: boolean;
  fullWidth?: boolean;
}

export function getBackgroundColor(props: ButtonProps): string {
  if (props.disabled) {
    return "var(--placeholder-color)";
  }

  if (props.active) {
    return "var(--light-white)";
  }

  switch (props.variant) {
    case "primary":
    default:
      return "var(--focus-green)";

    case "secondary":
    case "danger":
      return "white";

    case "tertiary":
      return "var(--background-gray)";

    case "transparent":
      return "transparent";
  }
}

export function getPadding(props: ButtonProps): string {
  switch (props.size) {
    case "large":
      return "0.75rem 1.25rem";
    case "medium":
    default:
      return "0.5rem 0.625rem";
    case "small":
      return "0.375rem 0.5rem";
    case "xsmall":
      return "0.25rem 0.5rem";
  }
}

export function getFontSize(props: ButtonProps): string {
  switch (props.size) {
    case "xsmall":
      return "var(--font-size-xxsmall)";
    case "small":
      return "var(--font-size-xsmall)";
    case "medium":
      return "var(--font-size-small)";
    default:
      return "var(--font-size-medium)";
  }
}

export function getSvgSize(props: ButtonProps): string {
  switch (props.size) {
    case "xsmall":
      return "0.75rem";
    case "small":
      return "1rem";
    case "medium":
    case undefined:
      return "1.25rem";
    case "large":
      return "1.5rem";
    default:
      const exhaust: never = props.size;
      return exhaust;
  }
}

export function getGapSize(props: ButtonProps): string {
  switch (props.size) {
    case "xsmall":
    case "small":
      return "0.25rem";
    default:
      return "0.5rem";
  }
}

export function getHoverBackgroundColor(props: ButtonProps): string {
  if (props.disabled) {
    return "var(--placeholder-color)";
  }

  switch (props.variant) {
    case "primary":
      return "var(--green)";

    case "secondary":
      return "var(--background-green-solid)";

    case "tertiary":
      return "var(--border-gray-light)";

    default:
    case "transparent":
      return "var(--background-green)";

    case "danger":
      return "var(--background-error-color)";
  }
}

export function getActiveBackgroundColor(props: ButtonProps): string {
  if (props.disabled) {
    return "var(--placeholder-color)";
  }

  if (props.active) {
    return "white";
  }

  switch (props.variant) {
    case "primary":
    default:
      return "var(--dark-green)";

    case "transparent":
    case "secondary":
      return "transparent";

    case "tertiary":
      return "var(--border-gray-light)";
    case "danger":
      return "var(--background-error-color)";
  }
}

export function getDropShadow(props: ButtonProps): string {
  switch (props.variant) {
    default:
      return "var(--drop-shadow)";
    case "transparent":
      return "none";
  }
}

export function getHoverDropShadow(props: ButtonProps): string {
  switch (props.variant) {
    default:
      return "var(--drop-shadow-dark)";
    case "transparent":
      return "none";
  }
}

export function getHoverFontColor(props: ButtonProps): string {
  if (props.disabled) {
    return "white";
  }

  switch (props.variant) {
    case "primary":
    default:
      return "white";

    case "secondary":
      return "var(--dark-green)";

    case "transparent":
      return "var(--green)";

    case "tertiary":
      return "var(--primary-black)";

    case "danger":
      return "var(--dark-error-color)";
  }
}

export function getFontColor(props: ButtonProps): string {
  if (props.disabled) {
    return "white";
  }

  switch (props.variant) {
    case "primary":
    default:
      return "white";

    case "transparent":
    case "secondary":
      return "var(--focus-green)";

    case "tertiary":
      return "var(--secondary-black)";

    case "danger":
      return "var(--error-color)";
  }
}

export function getFillColor(props: ButtonProps): string {
  if (props.disabled) {
    return "white";
  }

  switch (props.variant) {
    case "primary":
      return "white";

    case "transparent":
    case "secondary":
    default:
      return "var(--green)";

    case "tertiary":
      return "var(--dark-gray)";

    case "danger":
      return "var(--error-color)";
  }
}

export function getBorder(props: ButtonProps): string {
  if (props.disabled) {
    return "1px solid transparent";
  }

  if (props.active) {
    return "1px solid var(--highlight-green)";
  }

  switch (props.variant) {
    case "primary":
      return "1px solid var(--focus-green)";

    default:
    case "secondary":
      return "1px solid var(--green)";
    case "transparent":
      return "1px solid transparent";

    case "tertiary":
      return "1px solid var(--border-gray)";

    case "danger":
      return "1px solid var(--error-color)";
  }
}

export function getHoveredBorder(props: ButtonProps): string {
  if (props.disabled) {
    return "1px solid transparent";
  }

  switch (props.variant) {
    case "primary":
    case "secondary":
      return "1px solid var(--focus-green)";

    case "tertiary":
      return "1px solid var(--border-gray)";

    case "transparent":
    default:
      return "1px solid transparent";

    case "danger":
      return "1px solid var(--dark-error-color)";
  }
}
